import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, FolderPlus, Plus } from 'lucide-react';
import {
  api,
  ApiError,
  match,
  type MatchAccount,
  type Party,
} from '../api';
import { MatchShell } from './MatchShell';
import { usePinGate } from '../auth/pinGate';
import { useReadOnly } from '../ui/AppShell';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import {
  Button,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { EmptyState, RowSkeleton, SectionCard } from '../ui/components';

type StatusFilter = 'active' | 'archived' | 'all';

const STATUS_OPTIONS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
];

export function MatchAccountsPage() {
  const [status, setStatus] = useState<StatusFilter>('active');
  const [createOpen, setCreateOpen] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);

  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const readOnly = useReadOnly();

  const accountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', status],
    queryFn: () => match.listAccounts({ status }),
  });
  const balancesQ = useQuery<Array<{ matchAccountId: number; balance: string }>>({
    queryKey: ['match-balances'],
    queryFn: () => match.listBalances(),
  });
  const partiesQ = useQuery<Party[]>({
    queryKey: ['parties', 'all'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const allAccountsQ = useQuery<MatchAccount[]>({
    queryKey: ['match-accounts', 'all'],
    queryFn: () => match.listAccounts({ status: 'all' }),
  });

  const balancesById = useMemo(
    () => new Map((balancesQ.data ?? []).map((item) => [item.matchAccountId, item.balance])),
    [balancesQ.data],
  );
  const partiesById = useMemo(
    () => new Map((partiesQ.data ?? []).map((party) => [party.id, party])),
    [partiesQ.data],
  );

  const activeCount = (allAccountsQ.data ?? []).filter((account) => account.status === 'active').length;
  const accountCount = (allAccountsQ.data ?? []).length;
  const linkedCount = (allAccountsQ.data ?? []).filter((account) => account.linkedPartyId !== null).length;
  const archivedCount = Math.max(0, accountCount - activeCount);
  const netBalance = useMemo(
    () => (balancesQ.data ?? []).reduce((sum, item) => sum + Number(item.balance || '0'), 0),
    [balancesQ.data],
  );

  const invalidateLists = () => {
    void qc.invalidateQueries({ queryKey: ['match-accounts'] });
    void qc.invalidateQueries({ queryKey: ['match-balances'] });
  };

  const archiveMut = useMutation({
    mutationFn: (id: number) => match.archiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidateLists();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });
  const unarchiveMut = useMutation({
    mutationFn: (id: number) => match.unarchiveAccount(id),
    onSuccess: () => {
      setPageError(null);
      invalidateLists();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 423) return;
      setPageError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const onArchive = (id: number) => void withPin(() => archiveMut.mutateAsync(id));
  const onUnarchive = (id: number) => void withPin(() => unarchiveMut.mutateAsync(id));

  const loading = accountsQ.isLoading || balancesQ.isLoading;
  const kpiLoading = allAccountsQ.isLoading || balancesQ.isLoading;
  const accounts = accountsQ.data ?? [];
  const isEmpty = !loading && accounts.length === 0;

  const queueTitle =
    status === 'active'
      ? activeCount > 0
        ? `${activeCount} active match accounts ready`
        : 'Create the first match account'
      : status === 'archived'
      ? archivedCount > 0
        ? `${archivedCount} archived account${archivedCount === 1 ? '' : 's'} kept for history`
        : 'No archived accounts right now'
      : `${accountCount} accounts across active and archived lists`;

  return (
    <MatchShell
      title="Accounts"
      subtitle="Isolated match ledgers, quick balance scanning, and split targets"
      actions={
        !readOnly ? (
          <Button size="sm" onClick={() => { setPageError(null); setCreateOpen(true); }}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
            <span>New account</span>
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="order-2 md:order-1">
          {!kpiLoading && (
            <MetricStrip
              section="match"
              items={[
                { label: 'Accounts', value: String(accountCount), note: 'total match ledgers' },
                { label: 'Linked', value: String(linkedCount), note: 'linked to a party' },
                {
                  label: 'Net balance',
                  value: netBalance.toFixed(2),
                  note: 'across all accounts',
                  tone: netBalance < 0 ? 'neg' : netBalance > 0 ? 'pos' : 'default',
                },
              ]}
            />
          )}
        </div>

        <div className="order-1 md:order-2">
          <ActionQueueCard
            section="match"
            eyebrow="Account list"
            title={queueTitle}
            description="Keep source accounts, split targets, and archived history in one compact operator view."
            tone="neutral"
            meta={`${accounts.length} shown in the current filter`}
            actions={
              <div className="flex w-full flex-wrap items-center gap-2">
                {STATUS_OPTIONS.map((option) => {
                  const active = status === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setStatus(option.value)}
                      className="rounded-full border px-3 py-1.5 text-small font-medium transition-colors"
                      style={
                        active
                          ? { background: 'var(--sec-match)', color: '#fff', borderColor: 'var(--sec-match)' }
                          : { background: '#fff', color: 'var(--ink-muted)', borderColor: 'var(--border)' }
                      }
                    >
                      {option.label}
                    </button>
                  );
                })}
                {!readOnly && (
                  <Button size="sm" variant="secondary" className="ml-auto" onClick={() => setCreateOpen(true)}>
                    New account
                  </Button>
                )}
              </div>
            }
          />
        </div>

        {pageError && <ErrorBanner>{pageError}</ErrorBanner>}

        <SectionCard
          section="match"
          title="Match accounts"
          action={<span className="text-caption text-ink-muted">{accounts.length} shown</span>}
        >
          {loading ? (
            <RowSkeleton rows={6} />
          ) : isEmpty ? (
            <EmptyState
              section="match"
              icon={FolderPlus}
              title={status === 'archived' ? 'No archived accounts' : 'No accounts yet'}
              description={
                status === 'archived'
                  ? 'Archived accounts stay here for reference once they stop being part of your daily split flow.'
                  : 'Create the first match account to start quick entry and split distribution.'
              }
              action={
                !readOnly && status !== 'archived' ? (
                  <Button size="sm" onClick={() => setCreateOpen(true)}>
                    Create first account
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {accounts.map((account) => {
                const balance = balancesById.get(account.id) ?? '0';
                const linkedParty = account.linkedPartyId !== null ? partiesById.get(account.linkedPartyId) : null;
                const busy =
                  (archiveMut.isPending && archiveMut.variables === account.id) ||
                  (unarchiveMut.isPending && unarchiveMut.variables === account.id);
                return (
                  <MatchAccountTicket
                    key={account.id}
                    account={account}
                    balance={balance}
                    linkedPartyLabel={
                      linkedParty ? `${linkedParty.displayName} (${linkedParty.role})` : 'No linked party'
                    }
                    readOnly={readOnly}
                    busy={busy}
                    onArchive={() => onArchive(account.id)}
                    onUnarchive={() => onUnarchive(account.id)}
                  />
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>

      {createOpen && (
        <CreateMatchAccountDialog
          parties={partiesQ.data ?? []}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            invalidateLists();
          }}
        />
      )}
    </MatchShell>
  );
}

function MatchAccountTicket({
  account,
  balance,
  linkedPartyLabel,
  readOnly,
  busy,
  onArchive,
  onUnarchive,
}: {
  account: MatchAccount;
  balance: string;
  linkedPartyLabel: string;
  readOnly: boolean;
  busy: boolean;
  onArchive: () => void;
  onUnarchive: () => void;
}) {
  const direction = balance === '0' ? 'flat' : balance.startsWith('-') ? 'net outflow' : 'net inflow';

  return (
    <div className="overflow-hidden rounded-[24px] border border-border/70 app-shell-surface p-4 shadow-sm backdrop-blur">
      <a href={`/match/accounts/${account.id}`} className="block">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="truncate text-small font-semibold tracking-[-0.01em] text-ink">{account.name}</div>
              {account.status === 'archived' && <Pill tone="neutral">Archived</Pill>}
            </div>
            <div className="mt-1 text-[12px] text-ink-muted">{linkedPartyLabel}</div>
            <div className="mt-2 text-[12px] text-ink-muted">
              {account.notes?.trim() || 'Open the account to review splits, recent movement, and the isolated ledger.'}
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className={`font-mono text-[1rem] font-semibold tabular-nums ${balance.startsWith('-') ? 'text-neg' : balance === '0' ? 'text-ink-muted' : 'text-pos'}`}>
              {balance}
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">{direction}</div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-2 border-t border-border/70 pt-3">
          <span className="text-[12px] text-ink-muted">
            {account.status === 'active' ? 'Split target available' : 'History kept for reference'}
          </span>
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface text-ink-muted ring-1 ring-border/60">
            <ArrowRight className="h-4 w-4" strokeWidth={1.9} />
          </span>
        </div>
      </a>

      {!readOnly && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <a href={`/match/accounts/${account.id}`} className="inline-flex flex-1">
            <Button size="sm" variant="secondary" className="w-full justify-center">
              Open account
            </Button>
          </a>
          {account.status === 'active' ? (
            <Button size="sm" variant="ghost" className="flex-1 justify-center" disabled={busy} onClick={onArchive}>
              {busy ? <Spinner /> : 'Archive'}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" className="flex-1 justify-center" disabled={busy} onClick={onUnarchive}>
              {busy ? <Spinner /> : 'Unarchive'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CreateMatchAccountDialog({
  parties,
  onClose,
  onCreated,
}: {
  parties: Party[];
  onClose: () => void;
  onCreated: (created: MatchAccount) => void;
}) {
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [linkedPartyId, setLinkedPartyId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      match.createAccount({
        name: name.trim(),
        notes: notes.trim().length === 0 ? null : notes.trim(),
        linkedPartyId: linkedPartyId === '' ? null : Number(linkedPartyId),
      }),
    onSuccess: (created) => {
      onCreated(created);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) {
      setError('Name is required.');
      return;
    }
    createMut.mutate();
  };

  const partyOptions = [
    { value: '', label: '— none —' },
    ...parties.map((party) => ({
      value: String(party.id),
      label: `${party.displayName} (${party.role})`,
    })),
  ];

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex w-full max-w-xl flex-col overflow-hidden rounded-t-[28px] border border-border bg-surface shadow-elevated sm:rounded-[26px]">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink">New match account</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </header>
        <form className="overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              name="name"
              placeholder="e.g. Shared-paati"
              hint="Case-insensitive unique across all match accounts."
              autoFocus
            />
            <Select
              label="Linked party (optional)"
              value={linkedPartyId}
              onChange={(value) => setLinkedPartyId(value)}
              options={partyOptions}
              hint="Display-only link to a main-ledger party. Does not affect balances."
            />
            <TextArea
              label="Notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional description or context"
              hint="Metadata only."
            />
          </div>

          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Create account'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={createMut.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
